/**
 * The member's server session as the app sees it. A member logged out on the
 * server (logout in another tab, password changed elsewhere, session expired)
 * used to see sends spin and lists look empty; every request now goes through
 * the checks here, and a lost session sends the member to the login modal.
 */

/** Response header of WebUser::loginRequired() (a guest on a login-required endpoint). */
export const LOGIN_REQUIRED_HEADER = 'X-Login-Required';
/** Response header of Request::validateCsrfToken() refusing a token. */
export const CSRF_FAILED_HEADER = 'X-CSRF-Failed';
/** sessionStorage flag the login modal on home reads to say why it opened. */
export const SESSION_LOST_KEY = 'sessionLost';
/** The PHP login form (`user.loginUrl`), where a redirected request of a guest ends up. */
const LOGIN_FORM_PATH = '/user/credentials/form';

export interface SessionState {
  user: Record<string, any> | null;
  csrfToken: string | null;
}

// Whether the app believes a member is logged in: only then is a 403 or a
// login page a lost session (guests call public endpoints and may get either)
let memberSession = typeof window !== 'undefined' && Boolean(window.reactAppProps?.user);
let lost = false;
let probe: Promise<SessionState | null> | null = null;

/** Follows AppContext's user (ConversationProvider), set to false before a deliberate logout. */
export const setMemberSession = (active: boolean): void => {
  memberSession = active;
  if (active) {
    lost = false;
  }
};

export const isMemberSession = (): boolean => memberSession;

/**
 * Reads `GET /user/api/session` (the member's state or `user: null`, and a fresh
 * CSRF token); concurrent callers share one request. Null when it failed.
 */
export const fetchSession = (): Promise<SessionState | null> => {
  if (!probe) {
    probe = fetch('/user/api/session', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(async (response) => {
        if (!response.ok || !(response.headers.get('content-type') || '').includes('application/json')) {
          return null;
        }
        const data = await response.json();
        return { user: data?.user ?? null, csrfToken: data?.csrfToken ?? null };
      })
      .catch(() => null)
      .finally(() => { probe = null; });
  }
  return probe;
};

/** Where the login modal opens, bringing the member back to this page afterwards. */
export const loginPath = (): string => {
  const current = window.location.pathname + window.location.search;
  return window.location.pathname === '/' ? '/?login=1' : `/?login=1&returnUrl=${encodeURIComponent(current)}`;
};

/**
 * The session is gone: full page load of the login modal on home, which says
 * the member was logged out (SESSION_LOST_KEY). Once, however many requests fail.
 */
export const handleSessionLost = (): void => {
  if (lost) {
    return;
  }
  lost = true;
  memberSession = false;
  try {
    window.sessionStorage.setItem(SESSION_LOST_KEY, '1');
  } catch {
    // storage unavailable: the modal opens without the notice
  }
  window.location.assign(loginPath());
};

/** Whether the server answered that this request needed a logged-in member. */
export const isLoginRequired = (response: Response): boolean =>
  (response.status === 401 || response.status === 403) && response.headers.get(LOGIN_REQUIRED_HEADER) !== null;

const isHtml = (response: Response): boolean => (response.headers.get('content-type') || '').includes('text/html');

/** A response that looks like a login page: redirected to the login form, or redirected to HTML. */
export const looksLikeLoginPage = (response: Response): boolean => {
  if (!response.redirected) {
    return false;
  }
  try {
    if (new URL(response.url, window.location.href).pathname === LOGIN_FORM_PATH) {
      return true;
    }
  } catch {
    // unparseable URL: judge by the content type
  }
  return isHtml(response);
};

/**
 * Checks a member's response for a lost session (see the file comment): a
 * login-required 401/403 is one; a login page, or HTML where JSON was expected
 * (`expectJson`), is confirmed with `GET /user/api/session` first, since a
 * redirect to HTML can also be a page that is fine. True when it was lost.
 */
export const checkSessionLost = async (response: Response, expectJson = false): Promise<boolean> => {
  if (!memberSession || lost) {
    return lost;
  }
  if (isLoginRequired(response)) {
    handleSessionLost();
    return true;
  }
  if (looksLikeLoginPage(response) || (expectJson && response.ok && isHtml(response))) {
    const session = await fetchSession();
    if (session && session.user === null && memberSession) {
      handleSessionLost();
      return true;
    }
  }
  return false;
};

/** Reads (and forgets) whether the last page was left because the session was lost. */
export const takeSessionLostNotice = (): boolean => {
  try {
    const value = window.sessionStorage.getItem(SESSION_LOST_KEY);
    window.sessionStorage.removeItem(SESSION_LOST_KEY);
    return value === '1';
  } catch {
    return false;
  }
};
